/**
 * FixErrorsModals renders the 5 modal variants used by the fix-errors page.
 * Only one is open at a time, gated by the `modal` discriminated union from
 * useFixErrors. Maps each `kind` to one Modal block:
 *
 *  - 'partial-success'        → "Continue & place order" vs "Fix errors"
 *  - 'all-mandatory'          → "Fix errors" only (Continue disabled)
 *  - 'property-optional'      → "Cancel" vs "Continue & import properties"
 *  - 'order-optional'         → "Fix errors" vs "Continue & place order"
 *  - 'order-partial-optional' → same as order-optional, partial flow action
 */

import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Typography } from '@/components/ui/typography';

import type { FixErrorModal } from '../types/modal-state';

// ---- Types ----------------------------------------------------------------

interface FixErrorsModalsProps {
  modal: FixErrorModal;
  dismissModal: () => void;
  fixIssueLoading: boolean;
  popUpLoader: boolean;
  popUpLoaderForOrder: boolean;
  fixIssues: () => Promise<void>;
  checkAndContinue: () => void;
  importPropertiesWithNull: () => void;
  importOrdersWithNull: () => void;
  importOrdersWithNullPartially: () => void;
}

// ---- Shared body components -----------------------------------------------

const MandatoryErrorBody = () => (
  <div className="px-5 py-3">
    <Typography as="p" variant="body">
      Continuing to place the bulk order will ignore any order with an error in
      a required field. Click <strong className="font-semibold">Continue</strong> to
      proceed with the partial bulk order, or{' '}
      <strong className="font-semibold">Fix errors</strong> to go back.
    </Typography>
  </div>
);

const OptionalErrorBody = ({ isProperty }: { isProperty: boolean }) => {
  const entityPlural = isProperty ? 'properties' : 'orders';
  const entitySingular = isProperty ? 'properties' : 'order';
  return (
    <div className="px-5 py-3">
      <Typography as="p" variant="body">
        <strong className="font-semibold">
          Some {entityPlural} still have errors in optional fields.
        </strong>{' '}
        If you continue, the {entitySingular} will import successfully and these
        fields will appear blank in the system.
      </Typography>
    </div>
  );
};

// ---- Main component -------------------------------------------------------

const FixErrorsModals = ({
  modal,
  dismissModal,
  fixIssueLoading,
  popUpLoader,
  popUpLoaderForOrder,
  fixIssues,
  checkAndContinue,
  importPropertiesWithNull,
  importOrdersWithNull,
  importOrdersWithNullPartially,
}: FixErrorsModalsProps) => {
  switch (modal.kind) {
    case 'none':
      return null;

    case 'partial-success':
      return (
        <Modal
          open
          onOpenChange={dismissModal}
          title="Errors Detected in Required Fields"
          size="sm"
          footer={
            <>
              <Button
                type="button"
                variant="outline"
                disabled={fixIssueLoading}
                onClick={() => fixIssues()}
                className="w-full sm:w-auto"
              >
                {fixIssueLoading && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                Fix Errors
              </Button>
              <Button
                type="button"
                onClick={() => checkAndContinue()}
                className="w-full sm:w-auto"
              >
                Continue &amp; Place Order
              </Button>
            </>
          }
        >
          <MandatoryErrorBody />
        </Modal>
      );

    case 'all-mandatory':
      return (
        <Modal
          open
          onOpenChange={dismissModal}
          title="Errors Detected in Required Fields"
          size="sm"
          footer={
            <>
              <Button
                type="button"
                variant="outline"
                disabled={fixIssueLoading}
                onClick={dismissModal}
                className="w-full sm:w-auto"
              >
                {fixIssueLoading && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                Fix Errors
              </Button>
              <Button
                type="button"
                disabled
                onClick={() => checkAndContinue()}
                className="w-full sm:w-auto"
              >
                Continue &amp; Place Order
              </Button>
            </>
          }
        >
          <MandatoryErrorBody />
        </Modal>
      );

    case 'property-optional':
      return (
        <Modal
          open
          onOpenChange={dismissModal}
          title="Import Without Fixing Errors?"
          size="sm"
          footer={
            <>
              <Button
                type="button"
                variant="outline"
                onClick={dismissModal}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={popUpLoader}
                onClick={importPropertiesWithNull}
                className="w-full sm:w-auto"
              >
                {popUpLoader && <Loader2 className="h-4 w-4 animate-spin" />}
                Continue & import properties
              </Button>
            </>
          }
        >
          <OptionalErrorBody isProperty={true} />
        </Modal>
      );

    case 'order-optional':
      return (
        <Modal
          open
          onOpenChange={dismissModal}
          title="Errors Detected in Optional Fields"
          size="sm"
          footer={
            <>
              <Button
                type="button"
                variant="outline"
                onClick={dismissModal}
                className="w-full sm:w-auto"
              >
                Fix Errors
              </Button>
              <Button
                type="button"
                disabled={popUpLoaderForOrder}
                onClick={importOrdersWithNull}
                className="w-full sm:w-auto"
              >
                {popUpLoaderForOrder && <Loader2 className="h-4 w-4 animate-spin" />}
                Continue & place order
              </Button>
            </>
          }
        >
          <OptionalErrorBody isProperty={false} />
        </Modal>
      );

    case 'order-partial-optional':
      return (
        <Modal
          open
          onOpenChange={dismissModal}
          title="Errors Detected in Optional Fields"
          size="sm"
          footer={
            <>
              <Button
                type="button"
                variant="outline"
                onClick={dismissModal}
                className="w-full sm:w-auto"
              >
                Fix Errors
              </Button>
              <Button
                type="button"
                disabled={popUpLoaderForOrder}
                onClick={importOrdersWithNullPartially}
                className="w-full sm:w-auto"
              >
                {popUpLoaderForOrder && <Loader2 className="h-4 w-4 animate-spin" />}
                Continue & place order
              </Button>
            </>
          }
        >
          <OptionalErrorBody isProperty={false} />
        </Modal>
      );
  }
};

export default FixErrorsModals;
